import { diffDays, localDate, localParts, parseLocal } from './time';

export interface FormatPrefs {
  timeFormat: '12h' | '24h';
  dateFormat: 'us' | 'intl';
  units: 'mi' | 'km';
}

export const DEFAULT_PREFS: FormatPrefs = { timeFormat: '12h', dateFormat: 'us', units: 'mi' };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function monthName(month: number, long = false): string {
  return (long ? MONTHS_LONG : MONTHS)[month - 1] ?? '';
}

export function weekdayName(weekday: number, long = false): string {
  return (long ? DAYS_LONG : DAYS)[weekday] ?? '';
}

/** '7:05 PM' or '19:05'. */
export function formatClock(
  hour: number,
  minute: number,
  prefs: Pick<FormatPrefs, 'timeFormat'>,
): string {
  const mm = String(minute).padStart(2, '0');
  if (prefs.timeFormat === '24h') return `${String(hour).padStart(2, '0')}:${mm}`;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:${mm} ${hour < 12 ? 'AM' : 'PM'}`;
}

export function formatTime(ms: number, tz: string, prefs: Pick<FormatPrefs, 'timeFormat'>): string {
  const p = localParts(ms, tz);
  return formatClock(p.hour, p.minute, prefs);
}

function dateParts(date: string) {
  const p = parseLocal(date);
  const weekday = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  return { ...p, weekday };
}

/** 'Wed, Sep 30' (US) or 'Wed 30 Sep' (intl). */
export function formatDayShort(date: string, prefs: Pick<FormatPrefs, 'dateFormat'>): string {
  const p = dateParts(date);
  return prefs.dateFormat === 'us'
    ? `${weekdayName(p.weekday)}, ${monthName(p.month)} ${p.day}`
    : `${weekdayName(p.weekday)} ${p.day} ${monthName(p.month)}`;
}

/** 'Sep 30' or '30 Sep'. */
export function formatMonthDay(date: string, prefs: Pick<FormatPrefs, 'dateFormat'>): string {
  const p = dateParts(date);
  return prefs.dateFormat === 'us'
    ? `${monthName(p.month)} ${p.day}`
    : `${p.day} ${monthName(p.month)}`;
}

/** 'Sep 30, 2026' or '30 Sep 2026'. */
export function formatFullDate(date: string, prefs: Pick<FormatPrefs, 'dateFormat'>): string {
  const p = dateParts(date);
  return prefs.dateFormat === 'us'
    ? `${monthName(p.month)} ${p.day}, ${p.year}`
    : `${p.day} ${monthName(p.month)} ${p.year}`;
}

/**
 * 'Sep 30 to Oct 16, 2026', 'Oct 3 to 9, 2026', 'Dec 28, 2026 to Jan 4, 2027'.
 * Omits the year when `withYear` is false.
 */
export function formatDateRange(
  start: string,
  end: string,
  prefs: Pick<FormatPrefs, 'dateFormat'>,
  withYear = true,
): string {
  const a = dateParts(start);
  const b = dateParts(end);
  if (start === end) return withYear ? formatFullDate(start, prefs) : formatMonthDay(start, prefs);
  const us = prefs.dateFormat === 'us';
  if (a.year !== b.year) {
    return withYear
      ? `${formatFullDate(start, prefs)} to ${formatFullDate(end, prefs)}`
      : `${formatMonthDay(start, prefs)} to ${formatMonthDay(end, prefs)}`;
  }
  const year = withYear ? (us ? `, ${b.year}` : ` ${b.year}`) : '';
  if (a.month === b.month) {
    return us
      ? `${monthName(a.month)} ${a.day} to ${b.day}${year}`
      : `${a.day} to ${b.day} ${monthName(a.month)}${year}`;
  }
  return us
    ? `${monthName(a.month)} ${a.day} to ${monthName(b.month)} ${b.day}${year}`
    : `${a.day} ${monthName(a.month)} to ${b.day} ${monthName(b.month)}${year}`;
}

/** 'October 2026'. */
export function formatMonthYear(year: number, month: number): string {
  return `${monthName(month, true)} ${year}`;
}

/** '3 h 27 min', '45 min', '2 h', '0 min'. */
export function formatDuration(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  if (r === 0) return `${h} h`;
  return `${h} h ${r} min`;
}

/** Compact form for tight spaces: '3h 27m'. */
export function formatDurationCompact(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${r}m`;
}

const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export function formatNumber(n: number): string {
  return numberFormat.format(n);
}

/** '1,416 mi' or '2,279 km'. Input is always miles. */
export function formatDistance(miles: number, prefs: Pick<FormatPrefs, 'units'>): string {
  return prefs.units === 'mi'
    ? `${numberFormat.format(miles)} mi`
    : `${numberFormat.format(miles * 1.609344)} km`;
}

/** 'today', 'tomorrow', 'in 12 days', 'yesterday', '3 days ago', relative to today's date. */
export function formatRelativeDay(target: string, today: string): string {
  const d = diffDays(today, target);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  if (d > 1) return `in ${d} days`;
  return `${-d} days ago`;
}

/** 'just now', '2 min ago', '3 h ago', or a date. */
export function formatAgo(
  then: number,
  now: number,
  tz: string,
  prefs: Pick<FormatPrefs, 'dateFormat'>,
): string {
  const sec = Math.round((now - then) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return formatMonthDay(localDate(then, tz), prefs);
}

export function formatMoney(minor: number, currency: string): string {
  try {
    const f = new Intl.NumberFormat('en-US', { style: 'currency', currency });
    const digits = f.resolvedOptions().maximumFractionDigits ?? 2;
    return f.format(minor / 10 ** digits);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

/** Joins words naturally: 'A', 'A and B', 'A, B and C'. */
export function joinNatural(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** 'Night 2 of 3', 'Nights 2 and 3 of 3', 'Nights 2 to 7 of 10'. */
export function formatNights(from: number, to: number, total: number): string {
  if (from === to) return `Night ${from} of ${total}`;
  if (to === from + 1) return `Nights ${from} and ${to} of ${total}`;
  return `Nights ${from} to ${to} of ${total}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}
